// src/pages/dashboard/components/SalesTrendChart.jsx
import React, { useState, useEffect } from 'react';
import { TrendingUp } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { formatCurrency } from '../../../utils/currencyUtil';
import { getSalesTrend } from '../../../services/api/salesReport';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import styles from './SalesTrendChart.module.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SalesTrendChart = ({ shopId, dateRange, currency }) => {
  const { t } = useLocalization();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('7days');
  const [activeChart, setActiveChart] = useState('count');

  useEffect(() => {
    fetchTrendData();
  }, [shopId, period]);

  const fetchTrendData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        period,
        groupBy: 'date'
      };
      if (shopId && shopId !== 'all') {
        params.shopId = shopId;
      }
      const response = await getSalesTrend(params);
      setData(response);
    } catch (err) {
      console.error('Error fetching sales trend:', err);
      setError(err.message || t('dashboard.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  const getLabels = (chartData) => {
    return chartData.map(item => {
      const parts = item.label.split('-');
      if (parts.length >= 3) {
        return `${parts[1]}-${parts[2]}`;
      }
      return item.label;
    });
  };

  const renderChart = () => {
    if (!data || !data.trend || data.trend.length === 0) {
      return (
        <div className={styles.emptyChart}>
          <p>{t('dashboard.noDataForPeriod')}</p>
        </div>
      );
    }

    const chartData = data.trend;
    const labels = getLabels(chartData);

    const lineOptions = (formatFn) => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${formatFn(context.parsed.y)}`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: function(value) {
              return formatFn(value);
            }
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    });

    if (activeChart === 'count') {
      return (
        <Line
          data={{
            labels,
            datasets: [
              {
                label: t('dashboard.orderCount'),
                data: chartData.map(item => item.orderCount || 0),
                borderColor: 'rgb(102, 126, 234)',
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                borderWidth: 2,
                fill: true,
                tension: 0.4,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: 'rgb(102, 126, 234) ',
                pointBorderColor: '#fff',
                pointBorderWidth: 2
              }
            ]
          }}
          options={lineOptions((v) => formatNumber(v))}
        />
      );
    }

    if (activeChart === 'value') {
      return (
        <Line
          data={{
            labels,
            datasets: [
              {
                label: t('dashboard.salesValue'),
                data: chartData.map(item => item.totalValue || 0),
                borderColor: 'rgb(16, 185, 129)', 
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                borderWidth: 2,
                fill: true,
                tension: 0.4,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: 'rgb(16, 185, 129)',
                pointBorderColor: '#fff',
                pointBorderWidth: 2
              }
            ]
          }}
          options={lineOptions((v) => formatCurrency(v, currency))}
        />
      );
    }

    // quantity
    return (
      <Line
        data={{
          labels,
          datasets: [
            {
              label: t('dashboard.orderQuantity'),
              data: chartData.map(item => item.totalQuantity || 0),
              borderColor: 'rgb(245, 158, 11)',
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              borderWidth: 2,
              fill: true,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: 'rgb(245, 158, 11)',
              pointBorderColor: '#fff',
              pointBorderWidth: 2
            }
          ]
        }}
        options={lineOptions((v) => formatNumber(v))}
      />
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>{t('dashboard.salesTrend')}</h3>
        </div>
        <div className={styles.skeleton}>
          <div className={styles.skeletonChart}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>{t('dashboard.salesTrend')}</h3>
        </div>
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>{error}</p>
          <button onClick={fetchTrendData} className={styles.retryButton}>
            {t('dashboard.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h3>{t('dashboard.salesTrend')}</h3>
          <div className={styles.chartTypeToggle}>
            <button
              className={`${styles.toggleButton} ${activeChart === 'count' ? styles.active : ''}`}
              onClick={() => setActiveChart('count')}
            >
              {t('dashboard.orderCount')}
            </button>
            <button
              className={`${styles.toggleButton} ${activeChart === 'value' ? styles.active : ''}`}
              onClick={() => setActiveChart('value')}
            >
              {t('dashboard.salesValue')}
            </button>
            <button
              className={`${styles.toggleButton} ${activeChart === 'quantity' ? styles.active : ''}`}
              onClick={() => setActiveChart('quantity')}
            >
              {t('dashboard.orderQuantity')}
            </button>
          </div>
        </div>
        <div className={styles.periodSelector}>
          <button
            className={`${styles.periodButton} ${period === '7days' ? styles.active : ''}`}
            onClick={() => setPeriod('7days')}
          >
            {t('dashboard.last7Days')}
          </button>
          <button
            className={`${styles.periodButton} ${period === '14days' ? styles.active : ''}`}
            onClick={() => setPeriod('14days')}
          >
            {t('dashboard.last14Days')}
          </button>
          <button
            className={`${styles.periodButton} ${period === '30days' ? styles.active : ''}`}
            onClick={() => setPeriod('30days')}
          >
            {t('dashboard.last30Days')}
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.chartWrapper}>
          {renderChart()}
        </div>
      </div>
    </div>
  );
};

export default SalesTrendChart;